/**
 * Why Customers Love It Component
 * Displays product benefits with an image and bullet points
 */

'use client';

import styled from '@emotion/styled';
import { useTheme } from '@template-project/afrodita-components';
import { ProductBenefit } from '@/domain/entities/product.entity';
 

export interface WhyCustomersLoveItProps {
  title: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  benefits?: ProductBenefit[];
  disclaimerText?: string;
}

const Wrapper = styled.section`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme }) => `${theme.space['2xl']} ${theme.space.md}`};
`;

const MobileTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.variants.heading3.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  text-align: center;
  margin: ${({ theme }) => `0 0 ${theme.space.xl}`};
  color: ${({ theme }) => theme.colors.text.primary};

  @media (min-width: ${({ theme }) => theme.mediaQueries.md}) {
    display: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space['2xl']};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.space.xl};
  }
`;

const ImageTag = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${({ theme }) => theme.radius.regular};
  object-fit: cover;
`;

const ContentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.variants.heading3.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    display: none;
  }
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  line-height: 1.6;
  margin: 0;
`;

const BenefitsWrapper = styled.div<{ benefitsCount?: number }>`
  display: grid;
  grid-template-columns: ${({ benefitsCount }) =>
    benefitsCount && benefitsCount > 2 ? 'repeat(2, 1fr)' : '1fr'};
  gap: ${({ theme }) => theme.space.md};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
  }
`;

const SingleBenefit = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.sm};
`;

const CheckIcon = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.semantic.positive.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;

  &::after {
    content: '✓';
    color: ${({ theme }) => theme.colors.shades.white.color};
    font-size: ${({ theme }) => theme.fontSizes[2]}px;
    font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  }
`;

const BenefitText = styled.div`
  flex: 1;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  line-height: 1.6;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
`;

const BenefitCondition = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.shades[250].color};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  margin-top: ${({ theme }) => theme.space.xs};
  font-style: italic;
`;

const DisclaimerText = styled.p`
  color: ${({ theme }) => theme.colors.shades[250].color};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  line-height: 1.5;
  font-style: italic;
  margin-top: ${({ theme }) => theme.space.sm};
`;

export function WhyCustomersLoveIt({
  title,
  description,
  image,
  imageAlt = 'Product benefits',
  benefits,
  disclaimerText,
}: WhyCustomersLoveItProps) {
  const theme = useTheme();

  return (
    <Wrapper>
      <MobileTitle>{title}</MobileTitle>
      <Container>
        {image && (
          <ImageTag
            alt={imageAlt}
            src={image}
          />
        )}
        <ContentBlock>
          <Title>{title}</Title>
          {description && <Description>{description}</Description>}
          {benefits && benefits.length > 0 && (
            <BenefitsWrapper benefitsCount={benefits.length}>
              {benefits.map(({ id, content, condition }) => (
                <SingleBenefit key={id}>
                  <CheckIcon />
                  <BenefitText>
                    {content}
                    {condition && (
                      <BenefitCondition>{condition}</BenefitCondition>
                    )}
                  </BenefitText>
                </SingleBenefit>
              ))}
            </BenefitsWrapper>
          )}
          {disclaimerText && (
            <DisclaimerText>{disclaimerText}</DisclaimerText>
          )}
        </ContentBlock>
      </Container>
    </Wrapper>
  );
}

